import { Divider } from '@material-ui/core' // Using this Divider since new OS 3.0 does not have it
import { forwardRef } from 'react'
import { Badge, DrawerProps, Icon, IconButton, Typography } from '../../core'
import { styled } from '../../styles'
import { BaseThemeType, useMediaQuery } from '../../theme'

const StyledDrawerWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  width: 520,
  [theme.breakpoints.down(393)]: {
    width: 304,
  },
  maxWidth: '100vw',
}))

const StyledDrawerHeader = styled('div')(({ theme }) => ({
  padding: '20px 20px 0px 20px',
  [theme.breakpoints.down(393)]: {
    padding: '16px 16px 0px 16px',
  },
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
}))

const StyledDrawerUpperWrapper = styled('div')({
  width: '100%',
  maxWidth: 'calc(100vw - 48px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const StyledDrawerContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: 20,
  [theme.breakpoints.down(393)]: {
    padding: 16,
  },
  minHeight: 120,
}))

const StyledDrawerTag = styled('div')<DrawerProps>(({ theme, anchor }) => ({
  padding: 12,
  backgroundColor: theme.palette.yellow[800],
  position: 'absolute',
  ...((anchor === 'left' || !anchor) && {
    transform: 'translateY(-50%) rotate(90deg)',
  }),
  ...(anchor === 'right' && {
    transform: 'translateY(-50%) rotate(-90deg)',
  }),
  borderRadius: '4px 4px 0px 0px',
  ...((anchor === 'left' || !anchor) && {
    right: -63,
    [theme.breakpoints.down(393)]: {
      right: -61,
    },
  }),
  ...(anchor === 'right' && {
    left: -63,
    [theme.breakpoints.down(393)]: {
      left: -61,
    },
  }),
  top: 192,
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexDirection: anchor === 'left' || !anchor ? 'row-reverse' : 'row',
  color: theme.palette.neutralGrey[1300],
}))

const StyledDrawerDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.neutralGrey[700],
}))

const StyledDrawerHeaderTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  alignItems: 'center',
}))

export const DrawerContent = forwardRef<HTMLDivElement, DrawerProps>((props, ref) => {
  const {
    headerTitle,
    headerSubtitle,
    drawerContent,
    tagTitle,
    onTagClick,
    onClickHeaderIcon,
    anchor,
    badgeLabel,
  } = props

  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('lg'))
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down(393))

  return (
    <StyledDrawerWrapper>
      {tagTitle && (anchor === 'left' || !anchor || anchor === 'right') && (
        <StyledDrawerTag onClick={onTagClick} anchor={anchor}>
          <Typography variant={isMobile ? 'body3Medium' : 'body2Medium'}>{tagTitle}</Typography>
          <Icon icon={'keyboard_arrow_down'} size="small" />
        </StyledDrawerTag>
      )}
      <StyledDrawerUpperWrapper>
        <StyledDrawerHeader>
          <div>
            <StyledDrawerHeaderTitle>
              <Typography
                sx={{ color: (theme) => `${theme.palette.neutralGrey[1300]}` }}
                variant={isTablet ? 'body1SemiBold' : 'h5'}
              >
                {headerTitle}
              </Typography>
              {badgeLabel && <Badge size="small" color="primary" label={badgeLabel} />}
            </StyledDrawerHeaderTitle>
            <Typography
              sx={{ color: (theme) => `${theme.palette.neutralGrey[1000]}` }}
              variant={isTablet ? 'body3' : 'body1'}
            >
              {headerSubtitle}
            </Typography>
          </div>
          <IconButton
            sx={{ color: (theme) => `${theme.palette.neutralGrey[900]}`, width: 24, height: 24 }}
            size={'medium'}
            onClick={onClickHeaderIcon}
          >
            <Icon icon="close" />
          </IconButton>
        </StyledDrawerHeader>
        <div>
          <StyledDrawerDivider />
          <StyledDrawerContentWrapper>{drawerContent}</StyledDrawerContentWrapper>
        </div>
      </StyledDrawerUpperWrapper>
    </StyledDrawerWrapper>
  )
})
